import {
    resolveWorkspaceAvatarFromWorkspace,
    resolveWorkspaceImageDisplayUrl,
} from "@/lib/workspaceAvatar";

const resolvedSrcCache = new Map<string, string>();
const inflight = new Map<string, Promise<string | null>>();
const failedUntil = new Map<string, number>();
const FAIL_BACKOFF_MS = 60_000;

const PRELOAD_TIMEOUT_MS = 12_000;

function readBearerToken(): string | null {
    try {
        const token = localStorage.getItem("auth_token")?.trim();
        if (!token) return null;
        return token.startsWith("Bearer ") ? token.slice(7) : token;
    } catch {
        return null;
    }
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
    return Promise.race([
        promise,
        new Promise<null>((resolve) => {
            window.setTimeout(() => resolve(null), ms);
        }),
    ]);
}

function preloadDirectImage(src: string): Promise<string | null> {
    return new Promise((resolve) => {
        const img = new window.Image();
        img.onload = () => resolve(src);
        img.onerror = () => resolve(null);
        img.src = src;
    });
}

async function fetchProxiedWorkspaceImage(src: string): Promise<string | null> {
    const headers: Record<string, string> = {};
    const token = readBearerToken();
    if (token) headers.Authorization = `Bearer ${token}`;

    const res = await fetch(src, {
        headers,
        credentials: "include",
        cache: "no-store",
    });
    if (!res.ok) return null;

    const blob = await res.blob();
    return URL.createObjectURL(blob);
}

async function resolveWorkspaceImageSrc(src: string): Promise<string | null> {
    if (!src.trim()) return null;

    const failUntil = failedUntil.get(src);
    if (failUntil && Date.now() < failUntil) return null;
    if (failUntil) failedUntil.delete(src);

    const cached = resolvedSrcCache.get(src);
    if (cached) return cached;

    let resolved: string | null = null;

    if (
        src.startsWith("blob:") ||
        src.startsWith("data:") ||
        !src.startsWith("/api/workspace-image")
    ) {
        resolved = await preloadDirectImage(src);
    } else {
        resolved = await fetchProxiedWorkspaceImage(src);
    }

    if (resolved) {
        resolvedSrcCache.set(src, resolved);
    } else {
        failedUntil.set(src, Date.now() + FAIL_BACKOFF_MS);
    }

    return resolved;
}

export function getWorkspaceImageCachedSrc(src: string): string | null {
    if (!src.trim()) return null;
    return resolvedSrcCache.get(src) ?? null;
}

export function preloadWorkspaceImageSrc(src: string): Promise<string | null> {
    if (!src.trim()) return Promise.resolve(null);

    const cached = resolvedSrcCache.get(src);
    if (cached) return Promise.resolve(cached);

    const pending = inflight.get(src);
    if (pending) return pending;

    const promise = withTimeout(resolveWorkspaceImageSrc(src), PRELOAD_TIMEOUT_MS).finally(
        () => {
            inflight.delete(src);
        },
    );

    inflight.set(src, promise);
    return promise;
}

type WorkspaceAvatarPreloadInput = {
    imageUrl?: string | null;
    tags?: Array<{ key?: string; value?: string }> | null;
};

export async function preloadWorkspaceAvatars(
    workspaces: WorkspaceAvatarPreloadInput[],
): Promise<void> {
    if (typeof window === "undefined" || workspaces.length === 0) return;

    await Promise.all(
        workspaces.map(async (workspace) => {
            const avatar = resolveWorkspaceAvatarFromWorkspace({
                imageUrl: workspace.imageUrl,
                tags: workspace.tags,
                resolveImageUrl: resolveWorkspaceImageDisplayUrl,
            });

            if (avatar.mode !== "image" || !avatar.preview?.trim()) return;
            await preloadWorkspaceImageSrc(avatar.preview);
        }),
    );
}
